const DB_NAME = 'main';
const DB_VERSION = 1;
const STORE_NAME = 'cacheScreens';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };

    request.onsuccess = (event: Event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event: Event) => {
      const error = (event.target as IDBOpenDBRequest).error;
      console.error('IndexedDB openDatabase failed:', {
        dbName: DB_NAME,
        dbVersion: DB_VERSION,
        error: error?.message || error,
        errorName: error?.name,
        timestamp: new Date().toISOString()
      });
      reject(error);
    };
  });
}

export async function setItem(key: string, value: any): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put({ id: key, value });

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = (event: Event) => {
      const error = (event.target as IDBRequest).error;
      console.error('IndexedDB setItem failed:', {
        key,
        error: error?.message || error,
        errorName: error?.name,
        timestamp: new Date().toISOString()
      });
      reject(error);
    };
  });
}

export async function removeItem(key: string): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(key);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = (event: Event) => {
      const error = (event.target as IDBRequest).error;
      console.error('IndexedDB removeItem failed:', {
        key,
        error: error?.message || error,
        errorName: error?.name,
        timestamp: new Date().toISOString()
      });
      reject(error);
    };
  });
}

export async function getItem(key: string): Promise<any> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);

    request.onsuccess = (event: Event) => {
      const result = (event.target as IDBRequest).result;
      resolve([result?.id, result?.value]);
    };

    request.onerror = (event: Event) => {
      const error = (event.target as IDBRequest).error;
      console.error('IndexedDB getItem failed:', {
        key,
        error: error?.message || error,
        errorName: error?.name,
        timestamp: new Date().toISOString()
      });
      reject(error);
    };
  });
}

export async function getItemRegexp(command: string): Promise<any> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll(); // Fetch all entries to apply regex matching

    request.onsuccess = (event: Event) => {
      const entries = (event.target as IDBRequest).result;
      const matches: { key: string; value: any; specificity: number }[] = [];

      for (const entry of entries) {
        const key = entry.id;
        const value = entry.value;

        if (!key) continue;

        // Check for exact match first
        if (key === command) {
          matches.push({ key, value, specificity: -1 }); // Highest priority for exact matches
          continue;
        }

        // Check if the command is a prefix match for the key (before any placeholders)
        const placeholderIndex = key.indexOf('{{');
        if (placeholderIndex !== -1) {
          const keyPrefix = key.substring(0, placeholderIndex);
          if (command === keyPrefix) {
            // The command matches the key up to the first placeholder
            const specificity = (key.match(/{{\w+}}/g) || []).length;
            matches.push({ key, value, specificity });
            continue;
          }
        }

        // Escape special regex characters in the key
        const escapedKey = key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

        // Replace {{...}} placeholders with regex patterns
        const pattern = escapedKey.replace(/{{\w+}}/g, '[\\w\\-\\.~\\?&=]*'); // Make placeholders optional
        const regex = new RegExp(`^${pattern}$`);

        // Check if the command matches the regex
        if (regex.test(command)) {
          // Calculate specificity: fewer wildcards = higher specificity
          const specificity = (key.match(/{{\w+}}/g) || []).length;
          matches.push({ key, value, specificity });
        }
      }

      if (matches.length > 0) {
        // Sort matches by specificity (ascending: fewer wildcards = higher priority)
        matches.sort((a, b) => a.specificity - b.specificity);
        // Resolve with the best match
        resolve([matches[0].key, matches[0].value]);
      } else {
        // If no match is found, resolve with null
        resolve([null, null]);
      }
    };

    request.onerror = (event: Event) => {
      const error = (event.target as IDBRequest).error;
      console.error('IndexedDB getItemRegexp failed:', {
        command,
        error: error?.message || error,
        errorName: error?.name,
        timestamp: new Date().toISOString()
      });
      reject(error);
    };
  });
}
